import { createContext, createElement, useContext, type ReactNode } from 'react';

type PersonnelReadinessCloudState = {
    hasLoadedCloudProject?: boolean;
    hasLoadedPersonnel?: boolean;
};

export interface ProjectDataReadiness {
    pendingResources: readonly string[];
    status: 'pending' | 'ready';
}

export interface ProjectDataAuthority {
    remoteAuthority: boolean;
    authoritativeResources: readonly string[];
}

const LOCAL_PROJECT_DATA_AUTHORITY: ProjectDataAuthority = {
    remoteAuthority: false,
    authoritativeResources: [],
};

const ProjectDataAuthorityContext = createContext<ProjectDataAuthority>(LOCAL_PROJECT_DATA_AUTHORITY);

export function includeScriptBlockAuthority(input: {
    projectAuthority: ProjectDataAuthority;
    scriptBlocksAuthoritative: boolean;
    scriptBlocksRequired: boolean;
}): ProjectDataAuthority {
    if (!input.projectAuthority.remoteAuthority || !input.scriptBlocksRequired || !input.scriptBlocksAuthoritative) {
        return input.projectAuthority;
    }
    return {
        ...input.projectAuthority,
        authoritativeResources: Array.from(new Set([
            ...input.projectAuthority.authoritativeResources,
            'scriptBlocks',
        ])),
    };
}

export function deriveProjectDataReadiness(input: {
    authority: ProjectDataAuthority;
    requiredResources: readonly string[];
}): ProjectDataReadiness {
    if (!input.authority.remoteAuthority) return { pendingResources: [], status: 'ready' };

    const authoritative = new Set(input.authority.authoritativeResources);
    const pendingResources = Array.from(new Set(input.requiredResources))
        .filter(resource => !authoritative.has(resource));
    return {
        pendingResources,
        status: pendingResources.length > 0 ? 'pending' : 'ready',
    };
}

export function ProjectDataAuthorityProvider({
    children,
    value,
}: {
    children: ReactNode;
    value: ProjectDataAuthority;
}) {
    return createElement(ProjectDataAuthorityContext.Provider, { value }, children);
}

export function useProjectDataAuthority(): ProjectDataAuthority {
    return useContext(ProjectDataAuthorityContext);
}

export function hasAuthoritativePersonnelReadModel(cloud: PersonnelReadinessCloudState): boolean {
    return cloud.hasLoadedPersonnel === true;
}
